import { TabContext, TabList, TabPanel } from "@mui/lab";
import {
    Box,
    Button,
    FormControlLabel,
    Stack,
    Switch,
    Tab,
    Typography,
} from "@mui/material";
import { Container } from "@mui/system";
import { observer } from "mobx-react-lite";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import CollageService from "../components/ExamplesService/CollageService";
import ServiceList from "../components/ExamplesService/ServiceList";
import ServiceCategories from "../components/ServiceCategories";
import PageHeader from "../components/PageHeader";
import { EXAMPLES_ROUTE } from "../utils/consts";
import { Context } from "..";

const Examples = observer(() => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { clinic } = useContext(Context);
    const tabValues = useMemo(() => new Set(["1", "2", "3"]), []);
    const tabFromQuery = searchParams.get("tab");
    const initialValue = tabValues.has(tabFromQuery) ? tabFromQuery : "1";
    const [value, setValue] = useState(initialValue);
    const [twoColumns, setTwoColumns] = useState(
        clinic?.serviceButtonsInTwoColumns !== false,
    );
    const [layoutLoading, setLayoutLoading] = useState(false);
    const handleChange = (event, newValue) => {
        if (!tabValues.has(newValue)) {
            return;
        }
        setValue(newValue);
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                next.set("tab", newValue);
                return next;
            },
            { replace: true },
        );
    };

    useEffect(() => {
        setTwoColumns(clinic?.serviceButtonsInTwoColumns !== false);
    }, [clinic?.serviceButtonsInTwoColumns]);

    useEffect(() => {
        if (!tabValues.has(tabFromQuery)) {
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    next.set("tab", "1");
                    return next;
                },
                { replace: true },
            );
        }
    }, [setSearchParams, tabFromQuery, tabValues]);

    useEffect(() => {
        const currentTab = searchParams.get("tab");
        if (tabValues.has(currentTab) && currentTab !== value) {
            setValue(currentTab);
        }
    }, [searchParams, tabValues, value]);

    const handleLayoutToggle = async (event) => {
        const checked = event.target.checked;
        setLayoutLoading(true);
        try {
            const { changeServiceButtonLayout } =
                await import("../http/clinicAPI");
            const updatedClinic = await changeServiceButtonLayout(checked);
            if (clinic && typeof clinic.setClinicData === "function") {
                clinic.setClinicData(updatedClinic);
            }
            setTwoColumns(updatedClinic.serviceButtonsInTwoColumns !== false);
        } catch (error) {
            console.error("Error updating layout:", error);
            setTwoColumns(clinic?.serviceButtonsInTwoColumns !== false);
        } finally {
            setLayoutLoading(false);
        }
    };

    const addRoute =
        value === "1"
            ? `${EXAMPLES_ROUTE}/new`
            : value === "3"
              ? `${EXAMPLES_ROUTE}/collage/new`
              : null;
    const addButtonLabel =
        value === "1"
            ? "Добавить услугу"
            : value === "3"
              ? "Добавить до/после"
              : "";
    const titleByTab = {
        1: "Услуги клиники",
        2: "Категории услуг",
        3: "Результат «до/после» по услугам",
    };
    const pageTitle = titleByTab[value] || "Примеры услуг";

    return (
        <Container>
            <TabContext value={value}>
                <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
                    <TabList
                        aria-label="Tab"
                        onChange={handleChange}
                        textColor="secondary"
                        indicatorColor="secondary"
                    >
                        <Tab label="Услуги" value="1" />
                        <Tab label="Категории" value="2" />
                        <Tab
                            label="Результат «до/после» по услугам"
                            value="3"
                        />
                    </TabList>
                </Box>
                <Typography variant="h6" sx={{ mb: value === "2" ? 1 : 2 }}>
                    {pageTitle}
                </Typography>
                {value !== "2" && (
                    <PageHeader
                        showDivider={true}
                        cardSx={{
                            justifyContent: "flex-start",
                        }}
                        left={
                            <Stack
                                direction="row"
                                spacing={2}
                                alignItems="center"
                                flexWrap="wrap"
                            >
                                {addRoute && (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        size="small"
                                        onClick={() => navigate(addRoute)}
                                        sx={{
                                            height: 40,
                                            minHeight: 40,
                                            px: 2.5,
                                        }}
                                    >
                                        {addButtonLabel}
                                    </Button>
                                )}
                            </Stack>
                        }
                        right={
                            value === "1" ? (
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={!!twoColumns}
                                            onChange={handleLayoutToggle}
                                            color="primary"
                                            disabled={layoutLoading}
                                        />
                                    }
                                    label={
                                        <Typography
                                            sx={{ whiteSpace: "pre-wrap" }}
                                        >{`Отображение в боте кнопок в два столбца`}</Typography>
                                    }
                                />
                            ) : null
                        }
                    />
                )}
                <TabPanel value="1" sx={{ px: 0 }}>
                    <ServiceList />
                </TabPanel>

                <TabPanel value="2" sx={{ px: 0, pt: 0 }}>
                    <ServiceCategories />
                </TabPanel>

                <TabPanel value="3" sx={{ px: 0 }}>
                    <CollageService />
                </TabPanel>
            </TabContext>
        </Container>
    );
});

export default Examples;
